import React, { useRef, useCallback, useEffect } from 'react';
import {useEditing} from '@/state/EditingContext';
import { CLASS_COLORS, GRID, NEUTRAL_BUTTON_COLOR } from '@/utils/constants';
import { classSongMap, formatClassDuration } from '@/state/classSteps';

// Room after the last bar, and slack so the ruler never scrolls less far than the tracks.
const GRID_END_PADDING = 48;
const SCROLLBAR_ALLOWANCE = 24;
import { Ruler } from './Ruler';
import { TrackList } from './TrackList';
import { SkyBackdrop } from './SkyBackdrop';

export const TimelineArea = ({ tracks, totalBars, sectionMarkers, playheadBar = 0, loopRegion, loopEnabled, onLoopRegion, onToggleLoop, onSeekToBar, onMute, onSolo, onDelete, onRenameTrack, onMixDraft, onMixCommit, onKnockControl, armedTrackId, onArmTrack, onDuplicateTrack, onSendToBank, showTrackArms, producerMode, onSelectTrack, activeTool, selectedClipIds, onSelectClip, onMoveClip, onSliceClip, onDuplicateClip, onDeleteClip, onTrimClipEdge, onResetClip, onLoopClip, onEditPattern, onMakeUnique, onClipSound, kiosk, sessionBpm, pxPerBar = GRID.PX_PER_BAR, onQuickStart, classSession, unavailableTrackIds, allowClipSound = true, classHighlight, onViewportWidth }) => {
  const editing=useEditing();
  const [box,setBox]=React.useState(null);
  const marqueeMoved=useRef(false);
  const boxCleanup=useRef(null);
  useEffect(()=>()=>boxCleanup.current?.(),[]);
  const beginBox=e=>{
    if(e.button!==0||activeTool!=='Select'||!editing.onBoxSelect||e.target.closest('.ba-timeline-clip')||!e.target.closest('[data-testid^="track-grid-"]'))return;
    // Only the empty lane starts a marquee; menus and controls inside a lane must receive their click.
    if(e.target!==e.target.closest('[data-testid^="track-grid-"]'))return;
    boxCleanup.current?.();
    const node=scrollRef.current,startX=e.clientX,startY=e.clientY,id=e.pointerId;
    const initial=e.shiftKey?new Set(selectedClipIds):new Set();let ids=null;
    marqueeMoved.current=false;e.preventDefault();node.setPointerCapture?.(id);
    const move=event=>{if(event.pointerId!==id||Math.hypot(event.clientX-startX,event.clientY-startY)<4)return;marqueeMoved.current=true;
      const left=Math.min(startX,event.clientX),right=Math.max(startX,event.clientX),top=Math.min(startY,event.clientY),bottom=Math.max(startY,event.clientY);
      ids=new Set(initial);node.querySelectorAll('.ba-timeline-clip').forEach(el=>{const r=el.getBoundingClientRect();if(r.left<right&&r.right>left&&r.top<bottom&&r.bottom>top)ids.add(el.dataset.clipId);});
      const r=node.getBoundingClientRect();setBox({left:left-r.left+node.scrollLeft,top:top-r.top+node.scrollTop,width:right-left,height:bottom-top});};
    const clean=()=>{window.removeEventListener('pointermove',move);window.removeEventListener('pointerup',up);window.removeEventListener('pointercancel',cancel);window.removeEventListener('keydown',key);boxCleanup.current=null;if(node.hasPointerCapture?.(id))node.releasePointerCapture(id);setBox(null);};
    const up=event=>{if(event.pointerId!==id)return;move(event);clean();editing.onBoxSelect(ids||initial);};
    const cancel=()=>{clean();marqueeMoved.current=true;};const key=event=>{if(event.key==='Escape')cancel();};
    window.addEventListener('pointermove',move);window.addEventListener('pointerup',up);window.addEventListener('pointercancel',cancel);window.addEventListener('keydown',key);boxCleanup.current=clean;
  };
  const scrollRef = useRef(null);
  const rulerScrollRef = useRef(null);

  // Single scroll sync: when tracks area scrolls horizontally, ruler follows
  const handleScroll = useCallback(() => {
    if (scrollRef.current && rulerScrollRef.current) {
      rulerScrollRef.current.scrollLeft = scrollRef.current.scrollLeft;
    }
  }, []);

  const gridWidth = totalBars * pxPerBar;
  const classMap = classSession?.flow === 2 ? classSongMap(sessionBpm, 2, classSession.mapId) : null;

  useEffect(() => {
    const node = scrollRef.current;
    if (!node || !onViewportWidth) return undefined;
    const measure = () => onViewportWidth(Math.max(0, node.clientWidth - GRID.TRACK_HEADER_WIDTH));
    measure();
    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', measure);
      return () => window.removeEventListener('resize', measure);
    }
    const observer = new ResizeObserver(measure);
    observer.observe(node);
    return () => observer.disconnect();
  }, [onViewportWidth]);

  useEffect(() => {
    const node = scrollRef.current;
    if (!node) return;
    // The sticky track headers cover the left TRACK_HEADER_WIDTH of the viewport, so the grid
    // that is actually visible is [scrollLeft, scrollLeft + clientWidth - header].
    const x = playheadBar * pxPerBar;
    const visible = node.clientWidth - GRID.TRACK_HEADER_WIDTH;
    if (x < node.scrollLeft || x > node.scrollLeft + visible - 40) node.scrollLeft = Math.max(0, x - visible / 2);
  }, [playheadBar, pxPerBar]);

  return (
    <div data-testid="timeline-area" className="flex flex-col flex-1 overflow-hidden relative">
      {/* Ruler row — fixed height, scrolls horizontally in sync with tracks */}
      <div className="flex shrink-0" style={{ height: GRID.RULER_HEIGHT }}>
        {/* Fixed "TRACKS" label — does NOT scroll */}
        <div className="shrink-0 flex items-center px-3 border-r" style={{ width: GRID.TRACK_HEADER_WIDTH, background: 'var(--ba-surface-1)', borderColor: 'var(--ba-line)', borderBottom: '1px solid var(--ba-line)', zIndex: 3 }}>
          <span style={{ fontSize: 11, fontWeight: 600, color: 'var(--ba-text-3)' }}>TRACKS</span>
        </div>
        {/* Ruler scroll container — synced with tracks */}
        <div ref={rulerScrollRef} className="flex-1 overflow-hidden" style={{ scrollbarWidth: 'none' }}>
          {/* Wider than the grid by the tracks' right padding plus room for a vertical scrollbar, so
              the ruler can always scroll as far as the tracks (when it could not, the two playheads
              split apart). */}
          <div style={{ width: gridWidth + GRID_END_PADDING + SCROLLBAR_ALLOWANCE }}>
            <Ruler totalBars={totalBars} sectionMarkers={sectionMarkers} playheadBar={playheadBar} loopRegion={loopRegion} loopEnabled={loopEnabled} onLoopRegion={onLoopRegion} onToggleLoop={onToggleLoop} kiosk={kiosk} onClickBar={onSeekToBar} pxPerBar={pxPerBar}
              endBar={classMap?.length} endLabel={classMap ? `End ${formatClassDuration(classMap.length, sessionBpm)}` : null} />
          </div>
        </div>
      </div>

      {/* Track area — main scrollable region. The sky sits behind it and fills the visible grid
          (not the scrolled content), so it never stretches with the track count or song length. */}
      <div className="ba-track-region relative flex-1 min-h-0 flex flex-col">
      <SkyBackdrop left={GRID.TRACK_HEADER_WIDTH} />
      <div ref={scrollRef} data-timeline-scroll onPointerDown={beginBox} onClickCapture={e=>{if(marqueeMoved.current){e.stopPropagation();marqueeMoved.current=false;}}} className="flex-1 overflow-auto relative" style={{ zIndex: 1 }} onScroll={handleScroll}>
        {/* Inner container with full grid width for galaxy + tracks */}
        <div className="relative" style={{ minWidth: gridWidth + GRID.TRACK_HEADER_WIDTH + GRID_END_PADDING }}>
          {classMap && <div data-testid="class-end-grid-line" className="class-end-grid-line" style={{ left: GRID.TRACK_HEADER_WIDTH + classMap.length * pxPerBar }} />}
          {classHighlight?.targetId === 'outro-region' && <div data-testid="class-highlight-outro-region" className="class-highlight-region class-highlight" style={{ left: GRID.TRACK_HEADER_WIDTH + classHighlight.startBar * pxPerBar, width: (classHighlight.endBar - classHighlight.startBar) * pxPerBar }} />}
          {editing.range && <div className="ba-range-tracks" style={{left:GRID.TRACK_HEADER_WIDTH+editing.range.startBar*pxPerBar,width:(editing.range.endBar-editing.range.startBar)*pxPerBar}} />}
          {box && <div className="ba-marquee" style={box} />}
          {/* Tracks with headers */}
          <div className="relative" style={{ zIndex: 1 }}>
            <TrackList
              tracks={tracks} totalBars={totalBars} playheadBar={playheadBar}
              onMute={onMute} onSolo={onSolo} onDelete={onDelete} onRenameTrack={onRenameTrack} onMixDraft={onMixDraft} onMixCommit={onMixCommit}
              onKnockControl={onKnockControl} armedTrackId={armedTrackId} onArmTrack={onArmTrack} onDuplicateTrack={onDuplicateTrack} onSendToBank={onSendToBank} showTrackArms={showTrackArms} producerMode={producerMode}
              onSelectTrack={onSelectTrack}
              activeTool={activeTool} selectedClipIds={selectedClipIds}
              onSelectClip={onSelectClip} onMoveClip={onMoveClip}
              onSliceClip={onSliceClip} onDuplicateClip={onDuplicateClip} onDeleteClip={onDeleteClip}
              onTrimClipEdge={onTrimClipEdge} onResetClip={onResetClip} onLoopClip={onLoopClip} onEditPattern={onEditPattern} onMakeUnique={onMakeUnique} onClipSound={onClipSound} kiosk={kiosk}
              sessionBpm={sessionBpm}
              pxPerBar={pxPerBar}
              onQuickStart={onQuickStart}
              classSession={classSession}
              unavailableTrackIds={unavailableTrackIds}
              allowClipSound={allowClipSound}
              classHighlight={classHighlight}
            />
          </div>
        </div>
      </div>
      </div>
      {tracks.length === 0 && (
        <div
          data-testid="empty-state"
          className="absolute flex flex-col items-center justify-center gap-4"
          style={{ top: GRID.RULER_HEIGHT, left: GRID.TRACK_HEADER_WIDTH, right: 0, height: 6 * GRID.TRACK_HEIGHT, color: 'var(--ba-text-3)', zIndex: 2, pointerEvents: 'none' }}
        >
          <img src="/pizzul/happy.png" alt="Pizzul is happy to help" style={{ width: 92, height: 92, objectFit: 'contain' }} />
          <span style={{ fontSize: 14, fontWeight: 600 }}>Start with a feeling. Build from there.</span>
          <div className="flex gap-3 flex-wrap justify-center" style={{ pointerEvents: 'auto' }}>
            <button className="pizzul-quick-start pizzul-quick-start-primary qs-colored" style={{ '--qs': CLASS_COLORS.melody }} onClick={() => onQuickStart?.('melodies')}>Add a melody</button>
            <button className="pizzul-quick-start qs-colored" style={{ '--qs': CLASS_COLORS.drums }} onClick={() => onQuickStart?.('drums')}>Add drums</button>
            <button className="pizzul-quick-start qs-colored" style={{ '--qs': NEUTRAL_BUTTON_COLOR }} onClick={() => onQuickStart?.('packs')}>Pick a vibe</button>
          </div>
        </div>
      )}
    </div>
  );
};

export default TimelineArea;
